import React from 'react';
import { XMarkIcon, BoltIcon, DevicePhoneMobileIcon, ShieldCheckIcon, CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { trackEvent } from '../utils/analytics';

interface MobileAppPromptProps {
  onClose: () => void;
  onContinueWeb: () => void;
}

const MobileAppPrompt: React.FC<MobileAppPromptProps> = ({ onClose, onContinueWeb }) => {
  const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.notescrafter.app';

  const handleDownloadApp = () => {
    trackEvent('mobile_app_download_clicked', {
      source: 'landing_page_prompt',
      user_agent: navigator.userAgent,
    });
    window.open(PLAY_STORE_URL, '_blank');
  };

  const handleContinueWeb = () => {
    trackEvent('mobile_app_prompt_dismissed', {
      action: 'continue_web',
      source: 'landing_page_prompt',
    });
    onContinueWeb();
  };

  const handleClose = () => {
    trackEvent('mobile_app_prompt_dismissed', {
      action: 'close',
      source: 'landing_page_prompt',
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="relative w-full max-w-md mx-4 bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl overflow-hidden animate-slideUp">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200 z-10"
          aria-label="Close"
        >
          <XMarkIcon className="w-5 h-5 text-gray-600" />
        </button>

        {/* Content */}
        <div className="px-8 py-10 text-center">
          {/* Play Store Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 flex items-center justify-center transform hover:scale-105 transition-transform duration-300">
              {/* Google Play Icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-20 h-20"
                viewBox="0 0 24 24"
                fill="url(#playStoreGradient)"
              >
                <defs>
                  <linearGradient id="playStoreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#00D9FF" />
                    <stop offset="50%" stopColor="#00A8E1" />
                    <stop offset="100%" stopColor="#0084C8" />
                  </linearGradient>
                </defs>
                <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z" />
              </svg>
            </div>
          </div>

          {/* Heading */}
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Get the NotesCrafter App
          </h2>

          {/* Subheading */}
          <p className="text-gray-600 mb-2 text-sm">
            Experience the best mobile performance
          </p>

          {/* Recommended Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-full mb-6">
            <CheckCircleIcon className="w-5 h-5 text-green-600" />
            <span className="text-sm font-semibold text-green-700">
              Recommended for Mobile
            </span>
          </div>

          {/* Features List */}
          <div className="space-y-3 mb-8 text-left bg-gray-50 rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <SparklesIcon className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Completely free</span> - no hidden costs
              </p>
            </div>
            <div className="flex items-start gap-3">
              <BoltIcon className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Faster processing</span> & better performance
              </p>
            </div>
            <div className="flex items-start gap-3">
              <DevicePhoneMobileIcon className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Optimized for mobile</span> with native features
              </p>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheckIcon className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Secure & reliable</span> offline support
              </p>
            </div>
          </div>

          {/* Download Button */}
          <button
            onClick={handleDownloadApp}
            className="w-full group relative px-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 overflow-hidden mb-3"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-700 to-indigo-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-center justify-center gap-3">
              {/* Google Play Icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z" />
              </svg>
              <span>Download from Play Store</span>
            </div>
          </button>

          {/* Continue with Web */}
          <button
            onClick={handleContinueWeb}
            className="w-full px-6 py-3 text-gray-600 font-medium rounded-xl hover:bg-gray-100 transition-colors duration-200"
          >
            Continue with Web Version
          </button>

          {/* Small note */}
          <p className="text-xs text-gray-500 mt-4">
            You can always download the app later from the menu
          </p>
        </div>
      </div>
    </div>
  );
};

export default MobileAppPrompt;
